import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppItem } from "@/types/whatsapp";
import { toUserError } from "@/lib/api-error";

interface WhatsAppAssociarDialogProps {
  item: WhatsAppItem | null;
  onClose: () => void;
}

export function WhatsAppAssociarDialog({ item, onClose }: WhatsAppAssociarDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [aplicarAosDemais, setAplicarAosDemais] = useState(true);

  const { data: results } = useQuery({
    queryKey: ["whatsapp-freelancer-search", search],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("freelancers")
        .select("id, nome, email, telefone, is_active")
        .ilike("nome", `%${search}%`)
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
    enabled: search.length >= 2,
  });

  const associar = useMutation({
    mutationFn: () =>
      whatsappApi.associarFreelancer(item!.id, {
        freelancer_id: selectedId!,
        aplicar_aos_demais: aplicarAosDemais,
      }),
    onSuccess: (result) => {
      toast({
        title: "Freelancer associado",
        description:
          result.items_atualizados > 1
            ? `${result.freelancer_nome} associado a ${result.items_atualizados} itens.`
            : `${result.freelancer_nome} associado ao item.`,
      });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-run"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-run-items"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao associar",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setSearch("");
    setSelectedId(null);
    setAplicarAosDemais(true);
    onClose();
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Associar item a um freelancer</DialogTitle>
          <DialogDescription>
            Contato: {item?.contact_name ?? "sem nome"}
            {item?.telefone ? ` · ${item.telefone}` : ""}. Não encontramos esse
            telefone na base (ou ele bate com mais de um cadastro).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Input
            placeholder="Digite o nome do freelancer (mín 2 chars)..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {(results ?? []).length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-md border">
              {results!.map((freelancer) => (
                <div
                  key={freelancer.id}
                  onClick={() => {
                    setSelectedId(freelancer.id);
                    setSearch(freelancer.nome);
                  }}
                  className={`cursor-pointer p-2 text-sm hover:bg-muted ${
                    selectedId === freelancer.id ? "bg-muted font-medium" : ""
                  }`}
                >
                  <div>
                    {freelancer.nome}
                    {freelancer.is_active === false && (
                      <span className="ml-2 text-red-500">inativo</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[freelancer.email, freelancer.telefone].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 pt-1 text-sm">
            <Checkbox
              checked={aplicarAosDemais}
              onCheckedChange={(checked) => setAplicarAosDemais(checked === true)}
            />
            Aplicar aos demais itens pendentes desse telefone
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => associar.mutate()}
            disabled={!selectedId || associar.isPending}
            className="gap-1.5"
          >
            {associar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Associar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
